import ChainLogo from '@components/ChainLogo'
import TokenAvatar from '@components/TokenAvatar'
import { Chain } from '@plug-core/interfaces/common'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { Button, Heading, Surface, Text } from '@ui'
import React, { FC, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { HiChevronDoubleRight } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'

export type ExchangeViewType = 'swap' | 'mint' | 'bridge'

interface Props extends PropsWithChildren {
  fromTokenSymbol?: string
  toTokenSymbol?: string
  fromTokenImage?: string
  toTokenImage?: string
  fromTokenChain?: Chain
  toTokenChain?: Chain
  amount?: string
  toAmount?: string
  buttonTitle: string
  handleSubmit: () => void
  loading: boolean
  kind: ExchangeViewType
  error?: string
  step?: string
}

const ExchangeReview: FC<Props> = ({
  children,
  fromTokenSymbol,
  toTokenSymbol,
  fromTokenImage,
  toTokenImage,
  fromTokenChain,
  toTokenChain,
  amount,
  toAmount,
  buttonTitle,
  handleSubmit,
  loading,
  error,
  kind,
  step,
}) => {
  const navigate = useNavigate()

  const { t } = useTranslation()

  const renderTokenTile = (image?: string, chain?: Chain, value?: string, symbol?: string) => (
    <Surface variant="raised" className="flex min-w-0 flex-1 flex-col items-center gap-3">
      <span className="relative">
        <TokenAvatar size={44} logo={image} />
        {chain && (
          <span className="absolute -right-0.5 -bottom-1.5 grid place-items-center rounded-full bg-surface-2 p-1">
            <ChainLogo chain={chain} size={15} />
          </span>
        )}
      </span>
      <span className="flex w-full flex-col items-center gap-1">
        <Heading level={3} tabular truncate align="center" className="max-w-full">
          {value}
        </Heading>
        <Text variant="caption" tone="fg-2" align="center">
          {symbol}
        </Text>
      </span>
    </Surface>
  )

  return (
    <div className="flex flex-col gap-4">
      <Heading level={4} align="center">
        Review {t(`common.${kind}`)}
      </Heading>
      <div className="flex flex-col gap-2">
        <div className="relative flex items-stretch justify-center gap-2">
          {renderTokenTile(
            fromTokenImage,
            fromTokenChain,
            `${formatNumber(Number(amount))} `,
            fromTokenSymbol
          )}
          <span className="absolute top-1/2 left-1/2 grid size-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-hairline bg-sheet text-accent">
            <HiChevronDoubleRight aria-hidden="true" className="size-6" />
          </span>
          {renderTokenTile(
            toTokenImage,
            toTokenChain,
            formatNumber(Number(toAmount)),
            toTokenSymbol
          )}
        </div>
        {kind === 'mint' && (
          <Text variant="body-s" tone="fg-2" align="center" className="mt-2">
            {t('mintCk.delayWarning')}
          </Text>
        )}
        {kind === 'bridge' && (
          <Text variant="body-s" tone="fg-2" align="center" className="mt-2">
            {t('onesec.delayWarning')}
          </Text>
        )}
        {children}
      </div>
      <div className="mt-2 flex flex-col gap-4">
        <Button
          variant="primary"
          loading={loading}
          disabled={!!error || loading}
          onClick={handleSubmit}
          fullWidth
        >
          {step || buttonTitle}
        </Button>

        <Button variant="ghost" onClick={() => navigate(-1)} disabled={loading} fullWidth>
          Cancel
        </Button>
      </div>
    </div>
  )
}

export default ExchangeReview
